import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowLeft, Bell, Mail, TrendingUp, AlertTriangle, Activity, Calendar, Send } from 'lucide-react';
import TelegramSettings from '../components/TelegramSettings';

const NotificationSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [settings, setSettings] = useState({
    email_notifications: true,
    notify_trades: true,
    notify_stop_loss: true,
    notify_bot_status: true,
    notify_daily_summary: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/notifications/settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.updateNotificationSettings(settings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await api.sendTestNotification();
      if (response.data.status === 'success') {
        toast.success('Test email sent! Check your inbox.');
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2F6BFF]"></div>
      </div>
    );
  }

  const notificationOptions = [
    {
      key: 'email_notifications',
      icon: <Mail className="w-5 h-5" />,
      title: 'Email Notifications',
      description: 'Enable or disable all email notifications',
      master: true,
    },
    {
      key: 'notify_trades',
      icon: <TrendingUp className="w-5 h-5" />,
      title: 'Trade Executions',
      description: 'Get notified when a trade is executed',
      disabled: !settings.email_notifications,
    },
    {
      key: 'notify_stop_loss',
      icon: <AlertTriangle className="w-5 h-5" />,
      title: 'Stop Loss Alerts',
      description: 'Critical alerts when stop loss is triggered',
      disabled: !settings.email_notifications,
    },
    {
      key: 'notify_bot_status',
      icon: <Activity className="w-5 h-5" />,
      title: 'Bot Status Changes',
      description: 'Updates when bots start, pause, or stop',
      disabled: !settings.email_notifications,
    },
    {
      key: 'notify_daily_summary',
      icon: <Calendar className="w-5 h-5" />,
      title: 'Daily Summary',
      description: 'Receive a daily summary of your trading activity',
      disabled: !settings.email_notifications,
    },
  ];

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center">
          <Button
            data-testid="notifications-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#2F6BFF]/20 rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-[#2F6BFF]" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold font-['Outfit']">Notification Settings</h1>
              <p className="text-[#94A3B8]">Manage how you receive alerts and updates</p>
            </div>
          </div>

          <Card className="bg-[#15171C] border-[#1E293B]">
            <CardHeader>
              <CardTitle className="font-['Outfit']">Email Preferences</CardTitle>
              <CardDescription className="text-[#94A3B8]">
                Choose which notifications you want to receive via email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {notificationOptions.map((option, idx) => (
                <motion.div
                  key={option.key}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    option.master ? 'bg-[#2F6BFF]/10 border border-[#2F6BFF]/30' : 'bg-[#0B0D12]'
                  } ${option.disabled ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`text-${option.master ? '[#2F6BFF]' : '[#94A3B8]'}`}>
                      {option.icon}
                    </div>
                    <div>
                      <Label 
                        htmlFor={option.key} 
                        className={`text-${option.master ? '[#F5F7FA] font-semibold' : '[#F5F7FA]'} cursor-pointer`}
                      >
                        {option.title}
                      </Label>
                      <p className="text-xs text-[#94A3B8] mt-1">{option.description}</p>
                    </div>
                  </div>
                  <Switch
                    data-testid={`notification-toggle-${option.key}`}
                    id={option.key}
                    checked={settings[option.key]}
                    onCheckedChange={() => handleToggle(option.key)}
                    disabled={option.disabled}
                  />
                </motion.div>
              ))}

              {/* Test Email Button */}
              <div className="pt-4 border-t border-[#1E293B]">
                <Button
                  data-testid="send-test-email-btn"
                  onClick={sendTestEmail}
                  disabled={sendingTest || !settings.email_notifications}
                  variant="outline"
                  className="w-full border-[#2F6BFF] text-[#2F6BFF] hover:bg-[#2F6BFF]/10"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingTest ? 'Sending...' : 'Send Test Email'}
                </Button>
                <p className="text-xs text-[#94A3B8] text-center mt-2">
                  Send a test email to verify your notification setup
                </p>
              </div>

              {/* Save Button */}
              <Button
                data-testid="save-notification-settings-btn"
                onClick={saveSettings}
                disabled={saving}
                className="w-full bg-[#2F6BFF] hover:bg-[#2F6BFF]/90 rounded-sm"
                size="lg"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-[#15171C] border-[#1E293B] mt-6">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-[#F5F7FA] font-medium">Important Note</p>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    Stop Loss alerts are critical for risk management. We recommend keeping these enabled even if you disable other notifications.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Telegram Settings */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <TelegramSettings />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotificationSettings;
